import React, { useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Trash2, RotateCcw } from "lucide-react";

// Admin-only soft-archive / restore control for a client or provider account.
export function AccountArchiveControls({ id, name, role, archived, onDone }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const stop = (e) => e?.stopPropagation();

  const doArchive = async (e) => {
    stop(e);
    if (!reason.trim()) { toast.error("Enter a reason."); return; }
    setBusy(true);
    try {
      await api.post(`/admin/users/${id}/archive`, { reason: reason.trim() });
      toast.success(`${name} removed`);
      setOpen(false); setReason(""); onDone?.();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doRestore = async (e) => {
    stop(e); setBusy(true);
    try {
      await api.post(`/admin/users/${id}/restore`, {});
      toast.success(`${name} restored`);
      onDone?.();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (archived) {
    return (
      <Button data-testid={`restore-${role}-${id}`} size="sm" variant="outline" disabled={busy}
        onClick={doRestore} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">
        <RotateCcw className="mr-1 h-3.5 w-3.5" /> Restore
      </Button>
    );
  }

  return (
    <>
      <Button data-testid={`remove-${role}-${id}`} size="sm" variant="outline" disabled={busy}
        onClick={(e) => { stop(e); setOpen(true); }} className="border-red-300 text-red-600 hover:bg-red-50">
        <Trash2 className="mr-1 h-3.5 w-3.5" /> Remove
      </Button>
      {open && (
        <div data-testid={`remove-dialog-${id}`} onClick={(e) => { stop(e); setOpen(false); }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl" onClick={stop}>
            <h3 className="font-display text-lg font-bold text-slate-900">Remove {name}?</h3>
            <p className="mt-1 text-sm text-slate-500">
              The account is archived (hidden from lists, counts and revenue) and can no longer sign in.
              It stays in the database and can be restored.
            </p>
            <label className="mt-4 block text-xs font-semibold uppercase tracking-wide text-slate-500">Reason (required)</label>
            <textarea data-testid={`remove-reason-${id}`} value={reason} onChange={(e) => setReason(e.target.value)}
              rows={3} className="mt-1 w-full rounded-lg border border-slate-200 p-2 text-sm"
              placeholder="Why is this account being removed?" />
            <div className="mt-4 flex justify-end gap-2">
              <Button data-testid={`remove-cancel-${id}`} size="sm" variant="outline" disabled={busy}
                onClick={(e) => { stop(e); setOpen(false); }}>Cancel</Button>
              <Button data-testid={`remove-confirm-${id}`} size="sm" disabled={busy}
                onClick={doArchive} className="bg-red-600 text-white hover:bg-red-700">Remove account</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
